import { memo, useMemo, useState } from "react";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useLocation } from "react-router-dom";
import {
  AuraLayout,
  Button,
  Layout,
  Navbar,
  SupportV2Icon,
  Toast,
  TransferPanel,
} from "lola-framework-ui-test";
import { useTranslation } from "react-i18next";
import { APP_ROUTES, flavorClassName } from "../constants";
import { FlagCircle } from "../components/FlagCircle";
import { TransactionsHistory } from "../components/TransactionsHistory";
import { useExchangeRate, COUNTRY_CURRENCY } from "../hooks/useExchangeRate";
import { useOperationSSE } from "../hooks/useOperationSSE";
import { listOperations } from "../services/api";
import type { ITransaction } from "../interfaces/transactions";
import { TransactionsStatus } from "../utils/commons";
import "../styles/transactionspage.style.css";
import useRequest from "../hooks/useRequest";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { NavbarBackChevron } from "../components/NavbarBackChevron";
import { useClaimLink } from "../hooks/useClaimLink";
import { safeCapture } from "../utils/posthog";
import { useCallbackUrl } from "../store/usePartnerStore";

const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear().toString().slice(-2);
  return `${month}/${day}/${year}`;
};

const mapOperationStatusToTransactionStatus = (
  status: string
): TransactionsStatus => {
  switch (status) {
    case "processing":
      return TransactionsStatus.PROCESSING;
    case "completed":
      return TransactionsStatus.COMPLETED;
    case "failed":
      return TransactionsStatus.ERROR;
    case "cancelled":
    case "canceled":
      return TransactionsStatus.CANCELLED;
    default:
      return TransactionsStatus.PENDING;
  }
};

export const TransactionsPage = memo(() => {
  useClaimLink();
  const { t } = useTranslation();
  const {
    theme,
    setTransactions,
    setOperations,
    operations,
    exchangeRate,
    sentMoneyToast,
    setSentMoneyToast,
  } = useStore();
  const { session } = useSessionStore();
  const colorConfig = theme.whiteView;
  const {
    backgroundBtn,
    textColorBtn,
    dropzoneColors,
    iconColors,
    themeType,
    useSystemTheme,
    title,
    bodyCopy,
    buttonShowIcon,
    buttonSize,
  } = colorConfig;
  // inverted-icon is for dark color-mapping only. With useSystemTheme, themeType
  // tracks the OS while button/icon colors stay on theme.lightness — use iconColors.
  const useInvertedSupport = !useSystemTheme && themeType === "dark";
  const supportIconColors = useInvertedSupport ? dropzoneColors : iconColors;
  const { redirectTo } = useRouteWithToken();
  const callbackUrl = useCallbackUrl();
  const [transactions, setLocalTransactions] = useState<ITransaction[]>([]);
  const location = useLocation();

  useOperationSSE(session.data?.linkId);

  // Get currencies from session config
  const sourceCurrency =
    COUNTRY_CURRENCY[session.data?.config?.sourceCountry || "US"] || "USD";
  const destinationCurrency =
    COUNTRY_CURRENCY[session.data?.config?.destinationCountry || "MX"] || "MXN";

  // Fetch exchange rate from API
  const { loading: rateLoading } = useExchangeRate(
    sourceCurrency,
    destinationCurrency
  );

  // Format the exchange rate for display
  const displayRate = useMemo(
    () => (rateLoading ? "..." : exchangeRate?.rate?.toString() || "--"),
    [rateLoading, exchangeRate]
  );

  const handleFetchOperations = async () => {
    const linkId = session.data?.linkId;
    if (!linkId) return;

    const fetchOperations = async () => {
      try {
        const ops = await listOperations(linkId);
        setOperations(ops);
      } catch (error) {
        console.error("Failed to fetch operations:", error);
      }
    };

    void fetchOperations();
    return;
  };
  useRequest(handleFetchOperations, [session.data?.linkId, setOperations]);

  const handleFetchTransactions = async () => {
    if (!session.data?.linkId) return;

    const mappedTransactions: ITransaction[] = operations.map((op) => ({
      operationId: op.operationId,
      status: mapOperationStatusToTransactionStatus(op.status),
      amount: op.receiveAmount.toFixed(2),
      currency: op.destinationCurrency,
      date: formatDate(op.createdAt),
    }));

    // TODO: check this
    setLocalTransactions(mappedTransactions);
    setTransactions(mappedTransactions);

    safeCapture("ui.home.transactions.screen.view", {
      count: mappedTransactions.length,
    });
    return;
  };
  useRequest(handleFetchTransactions, [operations, session.data?.linkId]);

  const currencyExchangeLabel = useMemo(
    () =>
      destinationCurrency && exchangeRate?.sourceCurrency
        ? `${destinationCurrency}/${exchangeRate?.sourceCurrency}`
        : destinationCurrency,
    [destinationCurrency, exchangeRate]
  );

  const fullName = session.data?.userData?.name?.firstName ?? "";

  return (
    <AuraLayout
      colorConfig={colorConfig}
      className={`${flavorClassName}`}
    >
      <Layout.Header>
        <NavbarBackChevron
          visible={!location?.state?.from?.includes("sending-money-page")}
          onBackClick={() => {
            window.location.href = callbackUrl;
          }}
        >
          <Navbar
            title={t("transactions.nav.title")}
            align="center"
            noBackButton={location?.state?.from?.includes("sending-money-page")}
            onBackClick={() => {
              window.location.href = callbackUrl;
            }}
            icon={
              <SupportV2Icon
                className={useInvertedSupport ? "inverted-icon" : undefined}
                colors={supportIconColors}
              />
            }
            onIconClick={() => {
              safeCapture("ui.home.transactions.support_button.click");
              redirectTo(APP_ROUTES.SUPPORT_PAGE);
            }}
          />
        </NavbarBackChevron>
      </Layout.Header>
      <Layout.Content className={`transactions-section ${flavorClassName}`}>
        {fullName && (
          <h3 className="foreground hello-text">
            {t("transactions.hello", { name: fullName })}
          </h3>
        )}
        <section className="transfer-panel-container">
          <TransferPanel
            amount={displayRate}
            currency={currencyExchangeLabel}
            footerText={t("transactions.currentRate")}
            textColor={title}
            alternativeTextColor={bodyCopy}
            icon={
              <FlagCircle
                iso2={(
                  session.data?.config?.destinationCountry || "MX"
                ).toLowerCase()}
              />
            }
          />
        </section>
        <TransactionsHistory items={transactions.slice(0, 5)} />
        <section className="toast-container">
          {sentMoneyToast.visible && (
            <Toast
              visible={sentMoneyToast.visible}
              message={sentMoneyToast.message ?? ""}
              duration={2000}
              type="success"
              onClose={() =>
                setSentMoneyToast({
                  visible: false,
                  type: undefined,
                  message: null,
                })
              }
            />
          )}
        </section>
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          <Button
            background={backgroundBtn}
            color={textColorBtn}
            showIcon={buttonShowIcon}
            size={buttonSize ?? "small"}
            onClick={() => redirectTo(APP_ROUTES.TRANSFER_PAGE)}
          >
            {t("transactions.transfer")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
